import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../contexts/ThemeContext';
import { APP_NAME, API_CONFIG, API_ENDPOINTS } from '../helpers/constants';
import { Mail, Refresh } from '@mui/icons-material';
import { useToast } from '../contexts/ToastContext';

const EmailVerificationSent: React.FC = () => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const locationState = (location.state as { email?: string; passwordNotSet?: boolean }) || {};
  const email = locationState.email || '';
  // PR #677 review (M2) — for a removed user signing up again, the backend discards the
  // password typed into the form; without this, the owner's only clue is a toast on the
  // previous page, and they'd otherwise only find out when a password login later fails
  // with E007. CompanySignup.tsx sets this in navigation state for that case only.
  const passwordNotSet = Boolean(locationState.passwordNotSet);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleResendEmail = async () => {
    if (!email) {
      showError('Email address is required');
      return;
    }

    setIsResending(true);
    setResendSuccess(false);

    try {
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USER_AUTH.RESEND_VERIFICATION}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (data.detail && Array.isArray(data.detail)) {
          const errorMessages = data.detail.map((err: any) => err.msg || 'Validation error').join(', ');
          showError(errorMessages);
        } else if (data.message) {
          showError(data.message);
        } else {
          showError('Failed to resend verification email. Please try again.');
        }
        return;
      }

      if (data.success && data.email_sent) {
        setResendSuccess(true);
        showSuccess(data.message || 'Verification email sent successfully!');
      } else {
        showError(data.message || 'Failed to resend verification email.');
      }
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      showError('An error occurred while resending the verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Mail 
            sx={{ 
              fontSize: 64, 
              color: 'primary.main', 
              mb: 2 
            }} 
          />
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Check Your Email
          </Typography>
        </Box>
        
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<Mail />}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            We've sent a verification link to your inbox
          </Typography>
          {email && (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {email}
            </Typography>
          )}
        </Alert>

        {passwordNotSet && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              This account doesn't have a password yet. After verifying your email, use{' '}
              <RouterLink
                to="/forgot-password"
                style={{ color: muiTheme.palette.warning.dark, fontWeight: 600 }}
              >
                Forgot password
              </RouterLink>{' '}
              to set one.
            </Typography>
          </Alert>
        )}

        {resendSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Verification email has been resent successfully!
            </Typography>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Please click on the verification link in the email to verify your account. 
          The link will expire in 24 hours for security reasons.
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          If you don't see the email, please check your spam folder or click the button below to resend.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Button
            onClick={handleResendEmail}
            fullWidth
            variant="contained"
            startIcon={isResending ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            disabled={isResending || !email}
            sx={{ mb: 2 }}
          >
            {isResending ? 'Sending...' : 'Resend Email'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmailVerificationSent;

